import { Link } from "react-router-dom";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, ExternalLink, Lock, PenLine } from "lucide-react";
import { StatusPill, GovLink } from "./FormBadges";

export const AUDIENCE_OPTIONS = [["all", "Any audience"], ["employee", "Employee-facing"], ["manager", "HR Managers / Supervisors"]];
export const SOURCE_OPTIONS = [["all", "Any source"], ["lumis_original", "LUMIS HR Original Template"], ["government", "Official Government Form"], ["organization", "Organization-Provided Form"], ["catalog_only", "Catalog Entry Only"]];
export const STATUS_OPTIONS = [["all", "Any status"], ["existing", "Standard Template"], ["beta", "LUMIS HR Original Template — Beta"], ["draft", "New LUMIS HR Draft — Pending Review"], ["approved", "LUMIS HR Original Template — Approved"], ["government", "Official Government Form"], ["catalog_only", "Content Pending Review"]];

export function applyFilters(entries, f) {
  const q = f.q.trim().toLowerCase();
  return entries.filter((e) =>
    (!q || e.title.toLowerCase().includes(q) || e.category.toLowerCase().includes(q) || (e.purpose || "").toLowerCase().includes(q)) &&
    (f.category === "all" || e.category_id === f.category) &&
    (f.audience === "all" || (f.audience === "employee" ? e.audience !== "manager" : e.audience === f.audience)) &&
    (f.source === "all" || e.source === f.source) &&
    (f.status === "all" || e.status === f.status));
}

function FilterSelect({ value, onChange, options, testid, label }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} aria-label={label} className="h-9 text-xs bg-white border-slate-300"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function CatalogFilters({ filters, setFilters, categories, compact }) {
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const px = compact ? "m-" : "";
  return (
    <div className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-5"}`} data-testid="catalog-filters">
      <div className={`relative ${compact ? "" : "col-span-2 sm:col-span-1"}`}>
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" aria-hidden="true" />
        <Input data-testid={`${px}catalog-search`} aria-label="Search forms by name" value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search forms…" className="h-9 pl-8 text-xs border-slate-300 bg-white" />
      </div>
      <FilterSelect testid={`${px}filter-category`} label="Filter by category" value={filters.category} onChange={set("category")} options={[["all", "All categories"], ...categories.map((c) => [c.id, c.label])]} />
      <FilterSelect testid={`${px}filter-audience`} label="Filter by audience" value={filters.audience} onChange={set("audience")} options={AUDIENCE_OPTIONS} />
      <FilterSelect testid={`${px}filter-source`} label="Filter by source" value={filters.source} onChange={set("source")} options={SOURCE_OPTIONS} />
      <FilterSelect testid={`${px}filter-status`} label="Filter by status" value={filters.status} onChange={set("status")} options={STATUS_OPTIONS} />
    </div>
  );
}

function CatalogRow({ e, isManager, labels }) {
  const locked = e.audience === "manager" && !isManager;
  const usable = e.usable && !locked;
  return (
    <li data-testid={`catalog-row-${e.id}`} className="py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#0A2A5E] leading-snug">{e.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1"><StatusPill status={e.status} label={e.status_label} /><span className="text-[10px] text-slate-500">{e.audience_label}{e.version ? ` · v${e.version.replace(/^v/, "")}` : ""}</span></div>
        </div>
        {e.status === "government" ? (
          <GovLink entry={e} leavingText={labels.leaving} className="flex-shrink-0 p-1.5 rounded-md text-[#0A2A5E] hover:bg-[#FDECD0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623]"><span className="sr-only">Open official form</span><ExternalLink className="w-4 h-4" aria-hidden="true" /></GovLink>
        ) : usable ? (
          <Link to={`/app/forms/fill/${e.template_id}`} data-testid={`catalog-open-${e.id}`} aria-label={`Open ${e.title}`} className="flex-shrink-0 p-1.5 rounded-md text-[#0A2A5E] hover:bg-[#FDECD0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623]"><PenLine className="w-4 h-4" aria-hidden="true" /></Link>
        ) : (
          <span className="flex-shrink-0 p-1.5 text-slate-400" title={locked ? "HR Managers only" : labels.pending}><Lock className="w-4 h-4" aria-hidden="true" /><span className="sr-only">{locked ? "HR Managers only" : "Not available for use"}</span></span>
        )}
      </div>
    </li>
  );
}

// Compact accordion catalog (right rail on desktop; "Browse All Forms" panel on mobile)
export default function CatalogPanel({ entries, categories, isManager, labels, openCategory, onOpenChange }) {
  return (
    <Accordion type="multiple" value={openCategory} onValueChange={onOpenChange} data-testid="catalog-accordion" className="w-full">
      {categories.map((c) => {
        const list = entries.filter((e) => e.category_id === c.id);
        if (!list.length) return null;
        return (
          <AccordionItem key={c.id} value={c.id} className="border-slate-200">
            <AccordionTrigger data-testid={`catalog-cat-${c.id}`} className="py-2.5 text-sm font-semibold text-[#0A2A5E] hover:no-underline">
              <span className="flex-1 text-left">{c.label}</span><span className="text-[11px] font-normal text-slate-500 mr-2">{list.length}</span>
            </AccordionTrigger>
            <AccordionContent><ul className="pl-0.5">{list.map((e) => <CatalogRow key={e.id} e={e} isManager={isManager} labels={labels} />)}</ul></AccordionContent>
          </AccordionItem>
        );
      })}
      {entries.length === 0 && <p className="text-sm text-slate-500 py-6 text-center" data-testid="catalog-empty">No forms match these filters.</p>}
    </Accordion>
  );
}
